import {useState} from 'react'

import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount} from '@s-ui/react-hooks'
import {useLocation, useRouter} from '@s-ui/react-router'
import Layout from '@adv-mt/mt-layout-basic'
import NavigationRequireAuth from '@adv-mt/mt-navigation-require-auth'

import LinkFactory from '../../../components/LinkFactory/index.js'
import StepFormSellerPlate from './components/StepFormSellerPlate/index.js'
import StepSaleTypeSelection from './components/StepSaleTypeSelection/index.js'
import StepUploadImages from './components/StepUploadImages/index.js'
import StepVehicleConfirmation from './components/StepVehicleConfirmation/index.js'
import {getInitialProps} from './getInitialProps.js'
import Loading from './Loading.js'

const STEPS = {
  formSellerPlate: 'formSellerPlate',
  vehicleConfirmation: 'vehicleConfirmation',
  saleTypeSelection: 'saleTypeSelection',
  uploadImages: 'uploadImages'
}

const PublishMyAdWithPlate = ({__HTTP__ = {}, h1, helmet, pageName, plateVehicleResults, postalCode}) => {
  const {redirectTo} = __HTTP__
  const {pathname, search} = useLocation()
  const {replace} = useRouter()
  const {user} = useSuiContext()

  const initialStep = plateVehicleResults ? STEPS.vehicleConfirmation : STEPS.formSellerPlate

  const [adId, setAdId] = useState(null)
  const [currentStep, setCurrentStep] = useState(initialStep)
  const [destinationUrl, setDestinationUrl] = useState(null)
  const [fields, setFields] = useState(null)
  const [instantOffer, setInstantOffer] = useState(null)
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState(0)

  useMount(() => {
    if (redirectTo) replace(redirectTo)
  })

  if (redirectTo) return null

  const userData = {email: user?.email, name: user?.name, phone: user?.phone, postalCode: postalCode || user?.zipCode}

  const handleOnSelectVehicle = ({adId, destinationUrl, fields}) => {
    setAdId(adId)
    setDestinationUrl(destinationUrl)
    setFields(fields)
    setCurrentStep(STEPS.uploadImages)
  }

  const handleOnAppraisalFound = ({instantOffer: offer, vehicleSelected = 0}) => {
    setInstantOffer(offer)
    setSelectedVehicleIndex(vehicleSelected)
    setCurrentStep(STEPS.saleTypeSelection)
  }

  return (
    <>
      <Helmet {...helmet} />
      <Layout.FullWidth footerProps={{isSimple: true}} linkFactory={LinkFactory}>
        <NavigationRequireAuth currentPath={`${pathname}${search}`}>
          {currentStep === STEPS.formSellerPlate && (
            <StepFormSellerPlate h1={h1} onSelectVehicle={handleOnSelectVehicle} pageName={pageName} />
          )}
          {currentStep === STEPS.vehicleConfirmation && (
            <StepVehicleConfirmation
              onAppraisalFound={handleOnAppraisalFound}
              onSelectVehicle={handleOnSelectVehicle}
              plateVehicleResults={plateVehicleResults}
              userData={userData}
            />
          )}
          {currentStep === STEPS.saleTypeSelection && instantOffer && (
            <StepSaleTypeSelection
              instantOffer={instantOffer}
              km={plateVehicleResults?.km}
              onSelectVehicle={handleOnSelectVehicle}
              selectedVehicle={plateVehicleResults?.vehicles?.[selectedVehicleIndex]}
              userData={userData}
            />
          )}
          {currentStep === STEPS.uploadImages && (
            <StepUploadImages adId={adId} destinationUrl={destinationUrl} fields={fields} pageName={pageName} />
          )}
        </NavigationRequireAuth>
      </Layout.FullWidth>
    </>
  )
}

PublishMyAdWithPlate.displayName = 'PublishMyAdWithPlate'

PublishMyAdWithPlate.propTypes = {
  __HTTP__: PropTypes.shape({redirectTo: PropTypes.string}),
  h1: PropTypes.string,
  helmet: PropTypes.object,
  pageName: PropTypes.string,
  plateVehicleResults: PropTypes.shape({
    km: PropTypes.string,
    plate: PropTypes.string,
    vehicles: PropTypes.array
  }),
  postalCode: PropTypes.string
}

PublishMyAdWithPlate.getInitialProps = getInitialProps
PublishMyAdWithPlate.renderLoading = Loading

export default PublishMyAdWithPlate

export {STEPS}
